import { parsePhoneNumberFromString } from 'libphonenumber-js/min';
import { SERVICES } from '@site/data/services';

/**
 * Reaching a person: normalising the number they typed, choosing the channel,
 * and filling a saved reply.
 *
 * The site's form takes the phone as free text ("98140 12345", "+44 7700…",
 * "098140-12345"), so nothing here trusts its shape. India is the default
 * region because that is where nearly every lead is.
 */

export interface Reachable {
  name: string;
  email: string | null;
  phone: string | null;
  phone_e164: string | null;
}

export function phoneE164(p: Pick<Reachable, 'phone' | 'phone_e164'>): string | null {
  if (p.phone_e164) return p.phone_e164;
  if (!p.phone) return null;
  const parsed = parsePhoneNumberFromString(p.phone, 'IN');
  return parsed?.isValid() ? parsed.number : null;
}

/** "+91 98140 12345", the way people read it. */
export function phoneDisplay(p: Pick<Reachable, 'phone' | 'phone_e164'>): string | null {
  const e164 = phoneE164(p);
  if (!e164) return p.phone;
  return parsePhoneNumberFromString(e164)?.formatInternational() ?? e164;
}

export const isIndian = (e164: string | null) => Boolean(e164?.startsWith('+91'));

/**
 * The first button: WhatsApp for Indian numbers (that is where these clients
 * live), email for everyone else (it reads as professional abroad), call when
 * that is all we have. Docs: ADMIN_PLAN.md §4.3.
 */
export function primaryChannel(p: Reachable): 'whatsapp' | 'email' | 'call' | null {
  const e164 = phoneE164(p);
  if (e164 && isIndian(e164)) return 'whatsapp';
  if (p.email) return 'email';
  if (e164) return 'whatsapp';
  return null;
}

/** wa.me wants digits only: no '+', no spaces. */
export const whatsappHref = (e164: string, text = '') =>
  `https://wa.me/${e164.replace(/\D/g, '')}${text ? `?text=${encodeURIComponent(text)}` : ''}`;

export const telHref = (e164: string) => `tel:${e164}`;

export const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;

/** "Website development" -> "website development", but "AI agents" stays "AI agents". */
export function lowerFirst(s: string | null | undefined): string | null {
  if (!s) return null;
  return /^[A-Z][A-Z]/.test(s) ? s : s.charAt(0).toLowerCase() + s.slice(1);
}

/* ---------------------------------------------------------------- services */

const SERVICE_NAMES = new Map(SERVICES.map((s) => [s.slug, { name: s.name, short: s.short }]));

/** What they asked about, from the form's `interest` (a service slug or "unsure"). */
export function serviceLabel(slug: string | null | undefined, style: 'name' | 'short' = 'short'): string | null {
  if (!slug || slug === 'unsure') return null;
  return SERVICE_NAMES.get(slug)?.[style] ?? slug;
}

export const SERVICE_OPTIONS = SERVICES.map((s) => ({ value: s.slug, label: s.name }));

/* --------------------------------------------------------------- templates */

export interface TemplateVars {
  name: string;
  company?: string | null;
  service?: string | null;
  date?: string | null;
}

/**
 * Fill {{first_name}} {{name}} {{company}} {{service}} {{date}}. A variable with
 * no value becomes a neutral phrase rather than a hole, so a template never
 * sends "about ." to a real person.
 */
export function fillTemplate(body: string, v: TemplateVars): string {
  const values: Record<string, string> = {
    first_name: firstName(v.name),
    name: v.name,
    company: v.company?.trim() || 'your business',
    service: v.service?.trim() || 'your project',
    date: v.date?.trim() || 'the day you picked',
  };
  return body.replace(/\{\{\s*(\w+)\s*\}\}/g, (whole, key: string) => values[key] ?? whole);
}
